import { FC, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { CompanyInfo, CustomSubscriptionForm } from "Bands/components";
import {
  BAND_ACTIONS,
  IBandSubscription,
  ICustomSubscriptionParams,
  ICustomSubscriptionPatchParams,
} from "Bands/types";
import {
  clearActionMeta,
  dataMetaSelector,
  dispatchGetBandAction,
  editCustomSubscription,
  getInvoice,
  invoiceSelectorCreator,
} from "Bands/state";
import { IAPIState, ICardTheme, IFormButtons, IStatus } from "shared/types";
import { copy } from "shared/assets";
import { addTemporaryToast } from "shared/components/Toast";
import { Loader, Message, Modal } from "shared/components";
import { Wallet } from "shared/assets/icons";
import { dateStringToISO, formatDateTime, isoToUTCString, prefixWebsite } from "shared/utils";
import { useClearActionMetaOnUnMount, useSubscriptionError, useFetchCompany } from "Bands/utils";
import { API_ENDPOINTS } from "shared/constants";
import { Spacer } from "shared/layout";

const { adminDashboard } = copy;
const {
  customSubscriptions: { edit: editCopy },
} = adminDashboard;

interface Props {
  handleClose: () => void;
  subscription: IBandSubscription;
}

export const EditCustomSubscriptionModal: FC<Props> = ({ handleClose, subscription }) => {
  const dispatch = useDispatch();

  const { company } = useFetchCompany(subscription.company.id || "");

  const { api: getInvoiceApiMeta, id: invoiceId } = useSelector(
    dataMetaSelector(BAND_ACTIONS.getInvoice)
  );
  const invoiceIdSelector = useMemo(() => invoiceSelectorCreator(invoiceId || ""), [invoiceId]);
  const invoice = useSelector(invoiceIdSelector);

  useEffect(() => {
    // fetch invoice
    dispatch(
      getInvoice({
        subscription: API_ENDPOINTS.SUBSCRIPTION(subscription.id),
      })
    );
  }, [dispatch, subscription.id]);

  const { api: apiMeta } = useSelector(dataMetaSelector(BAND_ACTIONS.editCustomSubscription));

  useClearActionMetaOnUnMount(BAND_ACTIONS.editCustomSubscription);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      // refetch band to update band info
      const bandId = subscription.bands[0].id;
      if (bandId) {
        dispatchGetBandAction(dispatch, bandId);
      }

      dispatch(clearActionMeta(BAND_ACTIONS.editCustomSubscription));

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: editCopy.success,
        })
      );

      handleClose();
    }
  }, [apiMeta, dispatch, handleClose, subscription.bands]);

  const handleSubmit = (subscriptionData: ICustomSubscriptionParams) => {
    const { amount, invoiceUrl, invoiceDate, invoiceNumber, start, stop } = subscriptionData;

    const cleanSubscription = {
      start: start ? isoToUTCString({ iso: dateStringToISO(start), type: "start" }) : undefined,
      stop: stop ? isoToUTCString({ iso: dateStringToISO(stop), type: "end" }) : undefined,
      invoice: {
        amount,
        invoiceUrl: invoiceUrl ? prefixWebsite(invoiceUrl) : undefined,
        invoiceNumber,
        invoiceDate: dateStringToISO(invoiceDate || ""),
      },
    } as ICustomSubscriptionPatchParams;

    dispatch(
      editCustomSubscription({
        subscriptionId: subscription.id,
        subscriptionData: cleanSubscription,
      })
    );
  };

  const buttons: IFormButtons = useMemo(
    () =>
      ({
        primary: {
          children: copy.actions.edit,
          "data-testid": "edit_custom_subscription",
        },
        secondary: {
          onClick: handleClose,
          "data-testid": "cancel_custom_subscription",
        },
      } as IFormButtons),
    [handleClose]
  );

  const defaultValues: ICustomSubscriptionParams = useMemo(() => {
    const formattedStart = formatDateTime(subscription.start, {
      dateOnly: true,
    });
    const formattedStop = formatDateTime(subscription.stop, { dateOnly: true });

    let subscriptionValues = {};

    if (formattedStart && formattedStop) {
      subscriptionValues = {
        start: formattedStart ? formattedStart : undefined,
        stop: formattedStop ? formattedStop : undefined,
      };
    }

    if (!invoice) return subscriptionValues;

    return {
      ...subscriptionValues,
      ...invoice,
      invoiceDate: formatDateTime(invoice.date, { dateOnly: true }),
    };
  }, [invoice, subscription]);

  const { error } = useSubscriptionError(apiMeta?.error);

  const renderContent = () => {
    if (getInvoiceApiMeta?.status === IAPIState.failed) {
      return <Message status={IStatus.error}>{copy.errors.general}</Message>;
    }

    if (getInvoiceApiMeta?.status === IAPIState.pending) {
      return <Loader />;
    }

    return (
      <Spacer size="l">
        {company && <CompanyInfo company={company} cardTheme={ICardTheme.secondary} />}
        <CustomSubscriptionForm
          error={error}
          isSubmitting={apiMeta?.status === IAPIState.pending}
          handleSubmit={handleSubmit}
          buttons={buttons}
          apiError={apiMeta?.error}
          defaultValues={defaultValues}
        />
      </Spacer>
    );
  };

  return (
    <Modal icon={Wallet} title={editCopy.title} handleClose={handleClose}>
      {renderContent()}
    </Modal>
  );
};
